/**
 * What crafting would do to an item: the upgraded copy to compare against,
 * and what an upgrade or a reroll costs. Pure, so the panel only renders.
 */
import {
    ITEM_REROLL_COST_GROWTH,
    getAllEssenceTypes,
    isNamedItem,
    buildEquipment,
    MAX_ITEM_UPGRADE_LEVEL,
    resolveItemRerollCost,
    resolveItemUpgradeCost,
    resolveUpgradeStats,
    type EssencePool,
    type EssenceType,
    type UpgradeStat,
} from '@sorrem/shared';
import type { UIInventoryItem } from '@sorrem-client/types';
import { formatEssenceAmount } from '@sorrem-client/game/essence-integration';
import { toEquipment, toUIInventoryItem } from '@sorrem-client/ui/shared/inventory-item';

/** What the workbench is set to do to the selected item. */
export type CraftingMode = 'upgrade' | 'reroll';

/** One essence type in the vessel, now and after paying a cost. */
export interface VesselRow {
    readonly type: EssenceType;
    readonly held: number;
    /** What the cost takes of this type; 0 when it spends none. */
    readonly spent: number;
    /** What is left after paying, or null when the vessel holds too little of this type. */
    readonly after: number | null;
    /** How much more of this type the cost needs; 0 when it is covered. */
    readonly short: number;
}

export interface CraftingPreview {
    /** A named item (Unforgotten, Empyreal): never upgraded or rerolled. */
    readonly isNamed: boolean;
    /** What rerolling any one affix costs, or null when it has none. */
    readonly rerollCost: Partial<EssencePool> | undefined;
    readonly upgradeLevel: number;
}

export function resolveCraftingPreview(item: UIInventoryItem): CraftingPreview {
    const equipment = toEquipment(item);
    return {
        isNamed: isNamedItem(equipment),
        rerollCost: resolveItemRerollCost(equipment),
        upgradeLevel: item.instance?.upgradeLevel ?? 0,
    };
}

/** How many times the last reroll's cost the next one costs, in words (`ITEM_REROLL_COST_GROWTH`): "twice". */
export const REROLL_COST_GROWTH_WORDS = ITEM_REROLL_COST_GROWTH === 2 ? 'twice' : `${ITEM_REROLL_COST_GROWTH} times`;

/** A shortfall as a sentence, in essence order ("Your vessel is short 4 Sorrow and 2 Fury."), or null when nothing is short. */
export function describeShortfall(shortfall: Partial<EssencePool>): string | null {
    const parts = getAllEssenceTypes()
        .filter((type) => (shortfall[type] ?? 0) > 0)
        .map((type) => formatEssenceAmount(type, shortfall[type] ?? 0));
    if (parts.length === 0) {
        return null;
    }
    const list = parts.length === 1 ? parts[0] : `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;
    return `Your vessel is short ${list}.`;
}

/** Every essence type in the vessel, in essence order, with what a cost leaves of it (nothing spent when there is no cost). */
export function resolveVesselRows(banked: Partial<EssencePool>, cost: Partial<EssencePool> | null): VesselRow[] {
    return getAllEssenceTypes().map((type) => {
        const held = banked[type] ?? 0;
        const spent = cost?.[type] ?? 0;
        return {
            type,
            held,
            spent,
            after: spent > held ? null : held - spent,
            short: Math.max(0, spent - held),
        };
    });
}

/** One upgrade level of an item, for the upgrade path (#287). */
export interface UpgradePathRow {
    readonly level: number;
    /** The item at this level. */
    readonly item: UIInventoryItem;
    /** The stats upgrades raise, at this level. */
    readonly stats: readonly { readonly stat: UpgradeStat; readonly value: number }[];
    /** What this level costs on its own, from the level below it. */
    readonly cost: Partial<EssencePool> | undefined;
    /** What reaching this level costs from where the item is now; null for a level already reached. */
    readonly total: Partial<EssencePool> | null;
    readonly isReached: boolean;
}

/** `a` with `b` added, type by type; neither input changes. */
function withCostAdded(a: Partial<EssencePool>, b: Partial<EssencePool> | undefined): Partial<EssencePool> {
    const sum: Partial<EssencePool> = { ...a };
    for (const type of getAllEssenceTypes()) {
        const amount = b?.[type] ?? 0;
        if (amount > 0) {
            sum[type] = (sum[type] ?? 0) + amount;
        }
    }
    return sum;
}

/**
 * Every upgrade level of an item, +1 to the cap: its stats there, what the
 * level costs, and what reaching it costs from now. Empty for an item that
 * is never upgraded (a named item, or no rolled copy).
 */
export function resolveUpgradePath(item: UIInventoryItem): UpgradePathRow[] {
    const equipment = toEquipment(item);
    const instance = equipment.instance;
    if (!instance || isNamedItem(equipment)) {
        return [];
    }
    const rows: UpgradePathRow[] = [];
    let total: Partial<EssencePool> = {};
    for (let level = 1; level <= MAX_ITEM_UPGRADE_LEVEL; level += 1) {
        const below = buildEquipment(equipment, { ...instance, upgradeLevel: level - 1 });
        const atLevel = buildEquipment(equipment, { ...instance, upgradeLevel: level });
        const cost = resolveItemUpgradeCost(below);
        const isReached = level <= instance.upgradeLevel;
        if (!isReached) {
            total = withCostAdded(total, cost);
        }
        rows.push({
            level,
            item: toUIInventoryItem(atLevel),
            stats: resolveUpgradeStats(atLevel),
            cost,
            total: isReached ? null : total,
            isReached,
        });
    }
    return rows;
}

/** The not-yet-reached row an upgrade to `targetLevel` ends on, or undefined when that level is reached or off the path. */
export function findUpgradeTarget(path: readonly UpgradePathRow[], targetLevel: number): UpgradePathRow | undefined {
    return path.find((row) => row.level === targetLevel && !row.isReached);
}
